// src/pages/actividades/components/mobile/ActividadDetailSheet.tsx
// Lightweight detail sheet for tapping an activity card on mobile.
// For full editing, navigates to /actividades/:id.
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, RotateCcw, Clock, Calendar, Star, User,
  ChevronRight, ExternalLink,
} from 'lucide-react';
import { Button, Separator, StatusBadge, PriorityBadge } from '@/components/ui';
import { MobileSheet } from '@/components/layout/mobile/MobileSheet';
import { formatDate, formatMinutes, cn } from '@/lib/utils';
import type { ActividadListItem } from '@/types';

interface ActividadDetailSheetProps {
  actividad: ActividadListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (id: string) => void;
  onReopen: (id: string) => void;
}

export function ActividadDetailSheet({
  actividad,
  open,
  onOpenChange,
  onComplete,
  onReopen,
}: ActividadDetailSheetProps) {
  const navigate = useNavigate();

  if (!actividad) return null;

  const isCompleted = actividad.estado === 'COMPLETADA' || actividad.estado === 'CANCELADA';

  const handleOpenFull = () => {
    onOpenChange(false);
    navigate(`/actividades/${actividad.id}`);
  };

  return (
    <MobileSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Detalle"
      headerActions={
        <Button variant="ghost" size="sm" onClick={handleOpenFull} className="h-8 px-2 text-xs">
          <ExternalLink className="h-3.5 w-3.5 mr-1" />
          Abrir
        </Button>
      }
    >
      <div className="p-4 space-y-4">
        {/* Title */}
        <h2 className={cn(
          'text-lg font-semibold',
          isCompleted && 'line-through text-muted-foreground'
        )}>
          {actividad.titulo}
        </h2>

        {/* Status + Priority row */}
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={actividad.estado} />
          <PriorityBadge priority={actividad.prioridad} />
          {actividad.en_foco && (
            <span className="inline-flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400">
              <Star className="h-3 w-3 fill-current" /> En foco
            </span>
          )}
        </div>

        <Separator />

        {/* Metadata */}
        <div className="space-y-3">
          {actividad.cliente_nombre && (
            <div className="flex items-center gap-3 text-sm">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{actividad.cliente_nombre}</span>
            </div>
          )}
          {actividad.responsable_nombre && (
            <div className="flex items-center gap-3 text-sm">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>@{actividad.responsable_nombre}</span>
            </div>
          )}
          {actividad.fecha_agendada && (
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{formatDate(actividad.fecha_agendada)}</span>
              {actividad.hora_agendada && (
                <span className="text-muted-foreground">{actividad.hora_agendada}</span>
              )}
            </div>
          )}
          {actividad.tiempo_registrado_min > 0 && (
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{formatMinutes(actividad.tiempo_registrado_min)} registrados</span>
            </div>
          )}
          {(actividad.subtareas_count ?? 0) > 0 && (
            <div className="flex items-center gap-3 text-sm">
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{actividad.subtareas_count} subtareas</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Actions */}
        <div className="flex gap-2">
          {isCompleted ? (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                onReopen(actividad.id);
                onOpenChange(false);
              }}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir
            </Button>
          ) : (
            <Button
              variant="default"
              className="flex-1"
              onClick={() => {
                onComplete(actividad.id);
                onOpenChange(false);
              }}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Completar
            </Button>
          )}
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleOpenFull}
          >
            Ver completo
          </Button>
        </div>
      </div>
    </MobileSheet>
  );
}
